import { jsPDF } from 'jspdf'

export type AgreementKind = 'freelance' | 'nda' | 'service'
export type AgreementData = Record<string, string>

const USAGE_KEY = 'beltocraft.browser.tasks.v1'
const MAX_TASKS = 3

export function getUsage(): { completed: number; remaining: number } {
  try {
    const completed = Math.max(0, Number.parseInt(window.localStorage.getItem(USAGE_KEY) ?? '0', 10) || 0)
    return { completed, remaining: Math.max(0, MAX_TASKS - completed) }
  } catch {
    return { completed: 0, remaining: MAX_TASKS }
  }
}

export function consumeTask(): { ok: boolean; remaining: number } {
  const current = getUsage()
  if (current.remaining <= 0) return { ok: false, remaining: 0 }
  try { window.localStorage.setItem(USAGE_KEY, String(current.completed + 1)) } catch { /* Private browsing may reject storage; keep the tool usable. */ }
  return { ok: true, remaining: current.remaining - 1 }
}

export function resetUsageForTesting() {
  try { window.localStorage.removeItem(USAGE_KEY) } catch { /* no-op */ }
}

export function safeFileName(value: string, fallback: string) {
  const clean = value.trim().replace(/[^a-z0-9-_]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 72)
  return clean || fallback
}

export function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export function downloadDataUrl(dataUrl: string, fileName: string) {
  const anchor = document.createElement('a')
  anchor.href = dataUrl
  anchor.download = fileName
  anchor.click()
}

export function validatePdfFile(file: File) {
  if (file.size > 15 * 1024 * 1024) return 'Please choose a PDF smaller than 15 MB.'
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) return 'That file is not a PDF. Choose a file ending in .pdf.'
  return null
}

function nonEmpty(data: AgreementData, key: string) {
  return (data[key] ?? '').trim()
}

export function validateAgreement(kind: AgreementKind, data: AgreementData) {
  const requiredByKind: Record<AgreementKind, string[]> = {
    freelance: ['title', 'jurisdiction', 'effectiveDate', 'client', 'freelancer', 'scope', 'deliverables', 'fees', 'paymentSchedule'],
    nda: ['jurisdiction', 'effectiveDate', 'disclosingParty', 'receivingParty', 'purpose', 'confidentialInfo', 'confidentialityPeriod'],
    service: ['jurisdiction', 'effectiveDate', 'client', 'provider', 'serviceDescription', 'deliverables', 'fees', 'paymentSchedule'],
  }
  const missing = requiredByKind[kind].filter((key) => !nonEmpty(data, key))
  return missing
}

function line(value: string, label: string) {
  return `${label}: ${value.trim()}`
}

export function agreementTitle(kind: AgreementKind, data: AgreementData) {
  if (kind === 'freelance') return nonEmpty(data, 'title') || 'Freelance Services Agreement'
  if (kind === 'nda') return data.type === 'mutual' ? 'Mutual Non-Disclosure Agreement' : 'Non-Disclosure Agreement'
  return 'Professional Services Agreement'
}

export function buildAgreementSections(kind: AgreementKind, data: AgreementData) {
  const sections: { heading: string; body: string }[] = []
  if (kind === 'freelance') {
    sections.push({ heading: '1. Parties and effective date', body: `${line(data.client, 'Client')}\n${line(data.freelancer, 'Freelancer')}\n${line(data.effectiveDate, 'Effective date')}\n${line(data.jurisdiction, 'Supported jurisdiction')}` })
    sections.push({ heading: '2. Scope of services', body: `${line(data.scope, 'Scope')}\n${line(data.deliverables, 'Deliverables')}\n${line(data.milestones, 'Milestones and deadlines')}` })
    sections.push({ heading: '3. Fees, currency and payment', body: `${line(data.fees, 'Fees and currency')}\n${line(data.paymentSchedule, 'Payment schedule')}\n${line(data.revisions, 'Revisions and change requests')}` })
    sections.push({ heading: '4. Ownership and confidentiality', body: `${line(data.ownership, 'Ownership and intellectual property')}\n${line(data.confidentiality, 'Confidentiality')}` })
    sections.push({ heading: '5. Termination and disputes', body: `${line(data.termination, 'Termination')}\n${line(data.disputes, 'Dispute-resolution terms')}` })
  } else if (kind === 'nda') {
    sections.push({ heading: '1. Parties and purpose', body: `${line(data.disclosingParty, 'Disclosing party')}\n${line(data.receivingParty, 'Receiving party')}\n${line(data.type === 'mutual' ? 'Mutual' : 'One-way', 'Arrangement')}\n${line(data.purpose, 'Purpose')}\n${line(data.effectiveDate, 'Effective date')}\n${line(data.jurisdiction, 'Supported jurisdiction')}` })
    sections.push({ heading: '2. Confidential information', body: `${line(data.confidentialInfo, 'Definition')}\n${line(data.exclusions, 'Exclusions')}\n${line(data.permittedDisclosures, 'Permitted disclosures')}` })
    sections.push({ heading: '3. Confidentiality term and handling', body: `${line(data.confidentialityPeriod, 'Confidentiality period')}\n${line(data.returnDestruction, 'Return or destruction')}` })
  } else {
    sections.push({ heading: '1. Parties and effective date', body: `${line(data.client, 'Client')}\n${line(data.provider, 'Service provider')}\n${line(data.effectiveDate, 'Effective date')}\n${line(data.jurisdiction, 'Supported jurisdiction')}` })
    sections.push({ heading: '2. Services, deliverables and timeline', body: `${line(data.serviceDescription, 'Service description')}\n${line(data.deliverables, 'Deliverables')}\n${line(data.timeline, 'Timeline')}\n${line(data.milestones, 'Milestones')}` })
    sections.push({ heading: '3. Fees and responsibilities', body: `${line(data.fees, 'Fees and currency')}\n${line(data.paymentSchedule, 'Payment schedule')}\n${line(data.responsibilities, 'Responsibilities')}\n${line(data.revisions, 'Revisions and change requests')}` })
    sections.push({ heading: '4. Confidentiality, ownership and termination', body: `${line(data.confidentiality, 'Confidentiality')}\n${line(data.ownership, 'Ownership')}\n${line(data.termination, 'Termination')}` })
  }
  return sections.map((section) => ({ ...section, body: section.body.split('\n').filter((item) => !item.endsWith(': ')).join('\n') }))
}

export function createAgreementPdf(kind: AgreementKind, data: AgreementData) {
  const pdf = new jsPDF({ unit: 'pt', format: 'a4', compress: true })
  const margin = 54
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  let y = 66
  const drawHeader = () => {
    pdf.setFillColor(34, 86, 61)
    pdf.rect(0, 0, pageWidth, 8, 'F')
    pdf.setTextColor(34, 86, 61)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.text('BELTOcraft / browser-generated draft', margin, 35)
    pdf.setTextColor(32, 39, 32)
  }
  const ensureRoom = (height: number) => { if (y + height > pageHeight - 65) { pdf.addPage(); drawHeader(); y = 66 } }
  drawHeader()
  pdf.setTextColor(34, 86, 61)
  pdf.setFont('helvetica', 'bold')
  pdf.setFontSize(20)
  pdf.text(agreementTitle(kind, data), margin, y)
  y += 18
  pdf.setTextColor(104, 113, 104)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.text('Prepared from the fields entered in BELTOcraft. Review before use.', margin, y)
  y += 28
  for (const section of buildAgreementSections(kind, data)) {
    ensureRoom(70)
    pdf.setTextColor(34, 86, 61)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(11)
    pdf.text(section.heading, margin, y)
    y += 17
    pdf.setTextColor(50, 57, 51)
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(10)
    const lines = pdf.splitTextToSize(section.body, pageWidth - margin * 2) as string[]
    for (const wrapped of lines) { ensureRoom(15); pdf.text(wrapped, margin, y); y += 14 }
    y += 15
  }
  ensureRoom(94)
  pdf.setDrawColor(180, 196, 183)
  pdf.line(margin, y, pageWidth - margin, y)
  y += 24
  pdf.setTextColor(80, 91, 82)
  pdf.setFont('helvetica', 'normal')
  pdf.setFontSize(9)
  pdf.text('Signature blocks', margin, y)
  y += 22
  pdf.setDrawColor(130, 145, 133)
  pdf.line(margin, y, margin + 190, y); pdf.line(pageWidth / 2 + 15, y, pageWidth - margin, y)
  y += 12
  pdf.setFontSize(8)
  pdf.text(kind === 'nda' ? nonEmpty(data, 'disclosingParty') || 'Disclosing party' : nonEmpty(data, 'client') || 'Client', margin, y)
  pdf.text(kind === 'nda' ? nonEmpty(data, 'receivingParty') || 'Receiving party' : nonEmpty(data, 'freelancer') || nonEmpty(data, 'provider') || 'Service provider', pageWidth / 2 + 15, y)
  y += 32
  pdf.setTextColor(112, 122, 113)
  pdf.setFontSize(8)
  const disclaimer = 'Legal limitation: this draft is a document-preparation aid, not legal advice or a guarantee of enforceability. Supported jurisdiction and template limitations should be reviewed before use.'
  pdf.text(pdf.splitTextToSize(disclaimer, pageWidth - margin * 2), margin, y)
  return pdf.output('blob') as Blob
}

export function generateTypedSignatureDataUrl(name: string, font = 'italic 88px Georgia, serif') {
  const canvas = document.createElement('canvas')
  canvas.width = 960; canvas.height = 300
  const context = canvas.getContext('2d')!
  context.clearRect(0, 0, canvas.width, canvas.height)
  context.fillStyle = '#22563d'
  context.textAlign = 'center'; context.textBaseline = 'middle'; context.font = font
  context.fillText(name.trim() || 'Your name', canvas.width / 2, canvas.height / 2 + 14)
  return canvas.toDataURL('image/png')
}
